<template>
  <div id="app" :class="appBackgroundClass">
    <!-- Global app chrome. Hidden on the live table so the game stays immersive.
         Wrapped in a plain sticky div so positioning can't fight ProductNav's own
         scoped `position: relative` root rule. -->
    <div v-if="showNav" class="app-nav">
      <ProductNav />
    </div>

    <main class="app-main">
      <div class="transition-container min-h-screen">
        <RouterView v-slot="{ Component, route: activeRoute }">
          <Transition
            :name="transitionName"
            mode="out-in"
            :appear="false"
          >
            <component :is="Component" :key="activeRoute.path" />
          </Transition>
        </RouterView>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import type { RouteLocationNormalized } from 'vue-router'
import ProductNav from '@/components/product/ProductNav.vue'

const router = useRouter()
const transitionName = ref('fade')

// Dynamic background based on route
const appBackgroundClass = computed(() => {
  const path = router.currentRoute.value.path
  if (path.startsWith('/game/')) {
    return 'app-game-background'
  }
  return 'app-default-background'
})

// Hide the top nav on the live table (route name 'game') so it stays full-bleed.
// The pre-auth invitation route keeps the nav.
const showNav = computed(() => router.currentRoute.value.name !== 'game')

// Compute transition based on route order (like portal)
function computeTransition(to: RouteLocationNormalized, from: RouteLocationNormalized): string {
  const toOrder = (to.meta.order as number) ?? 0
  const fromOrder = (from.meta.order as number) ?? 0

  if (toOrder === fromOrder) {
    return 'slide-down'
  }

  return toOrder > fromOrder ? 'slide-left' : 'slide-right'
}

// Watch for route changes and update transition
watch(
  () => router.currentRoute.value,
  (to, from) => {
    if (from && to.path !== from.path) {
      transitionName.value = computeTransition(to, from)
    }
  }
)
</script>

<style scoped>
#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* Sticky global nav — sits above page content during scroll. */
.app-nav {
  position: sticky;
  top: 0;
  z-index: 40;
}

/* Full-width content area (no sidebar offset). */
.app-main {
  position: relative;
  flex: 1;
  width: 100%;
  overflow: hidden;
}

/* App-level backgrounds to prevent white flash during transitions */
.app-default-background {
  background: linear-gradient(to bottom, #2F4636 0%, #406149 26%, #EFE6CF 64%, #F3ECDA 100%);
  transition: background 0.5s ease-in-out;
}

.app-game-background {
  background: linear-gradient(135deg, #2F4636 0%, #21342A 100%);
  transition: background 0.5s ease-in-out;
}

/* Transitions are defined in main.css */
</style>
